import type { Insurance } from "@/types/insurance/insurance";
import { toFixedNumber } from "../utils";

// Lovpålagt arbeidsuførhet
export function legalOccupationalDisabilityCompensation(
	age: number,
	income: number, // Income in G
	disability: number // 1 - 0.01
): number {
	const MIN_COMPENSATION_PERCENT = 0.1;
	const REDUCTION_RATE = 0.05;
	const INCREASE_RATE = 0.035;
	const INCREASE_RATE_BEFORE_34 = 0.025;

	if (disability < 0.01) {
		console.error("Disability is lower than minimum");
		return 0;
	}

	if (age < 0) {
		console.error("Age is out of range");
		return 0;
	}

	if (disability > 1) {
		console.error("Disability is too high");
		return 0;
	}

	const baseCoverageCases = [
		{ min: 0, max: 7, value: 22 },
		{ min: 7, max: 8, value: 24 },
		{ min: 8, max: 9, value: 26 },
		{ min: 9, max: 10, value: 28 },
		{ min: 10, max: 99, value: 30 },
	];

	let baseCoverage: number = 0;

	baseCoverageCases.forEach((item) => {
		if (income > item.min && income <= item.max) {
			baseCoverage = item.value;
		}
	});

	if (baseCoverage === 0) {
		return 0;
	}

	baseCoverage = baseCoverage * disability;
	let sum: number = 0;
	age = Math.floor(age);

	if (age === 45 || age === 46) {
		sum = baseCoverage;
	}

	if (age <= 34) {
		sum = baseCoverage + baseCoverage * INCREASE_RATE_BEFORE_34 * (35 - age);
		sum += baseCoverage * INCREASE_RATE * 10;
	}

	if (age < 45 && age > 34) {
		sum = baseCoverage + baseCoverage * INCREASE_RATE * (45 - age);
	}

	if (age > 46) {
		sum = baseCoverage + baseCoverage * REDUCTION_RATE * (46 - age);
	}

	const minCoverage = MIN_COMPENSATION_PERCENT * baseCoverage;

	if (sum < minCoverage) {
		return toFixedNumber(minCoverage);
	}

	return toFixedNumber(sum);
}

// Utvidet arbeidsuførhet
export function additionalOccupationalDisabilityCompensation(
	insurance: Insurance | undefined,
	age: number,
	income: number,
	disability: number,
	minimumDisability: number = 0.01
): number {
	const MIN_COMPENSATION_PERCENT = 0.1;
	let baseCoverageParam: number;
	let reduction: number;
	let increase: number;
	let increaseBefore34: number;
	let minAge: number;
	let maxAge: number;

	try {
		baseCoverageParam = parseFloat(
			insurance?.params?.find((param) => param.type === "base-compensation")
				?.value as string
		);
		reduction =
			parseFloat(
				insurance?.params?.find((param) => param.type === "reduction")
					?.value as string
			) / 100;
		increase =
			parseFloat(
				insurance?.params?.find((param) => param.type === "increase")
					?.value as string
			) / 100;
		increaseBefore34 =
			parseFloat(
				insurance?.params?.find((param) => param.type === "increase-before-34")
					?.value as string
			) / 100;
		minAge = parseFloat(
			insurance?.params?.find((param) => param.type === "min-age")
				?.value as string
		);
		maxAge = parseFloat(
			insurance?.params?.find((param) => param.type === "max-age")
				?.value as string
		);
	} catch {
		return 0;
	}

	if (!baseCoverageParam || baseCoverageParam < 0) {
		return 0;
	}

	if (minimumDisability < 0.01 || minimumDisability > 1) {
		return 0;
	}

	if (disability < minimumDisability) {
		return 0;
	}

	if (disability > 1) {
		return 0;
	}

	if (!minAge) {
		minAge = 0;
	}

	if (!maxAge) {
		maxAge = 72;
	}

	if (!age || age < minAge || age >= maxAge) {
		return 0;
	}

	if (!income || income < 0 || income > 100) {
		return 0;
	}

	age = Math.floor(age);

	const baseCoverageCases = [
		{ min: 0, max: 7, value: baseCoverageParam },
		{ min: 7, max: 8, value: (baseCoverageParam * 12) / 11 },
		{ min: 8, max: 9, value: (baseCoverageParam * 13) / 11 },
		{ min: 9, max: 10, value: (baseCoverageParam * 14) / 11 },
		{ min: 10, max: 9999, value: (baseCoverageParam * 15) / 11 },
	];

	let baseCoverage: number = 0;

	baseCoverageCases.forEach((item) => {
		if (income > item.min && income <= item.max) {
			baseCoverage = item.value;
		}
	});

	if (baseCoverage === 0) {
		return 0;
	}

	baseCoverage = baseCoverage * disability;
	let sum: number = 0;

	if (age === 45 || age === 46) {
		sum = baseCoverage;
	}

	if (age <= 34) {
		sum =
			baseCoverage +
			baseCoverage * (increaseBefore34 ? increaseBefore34 * (35 - age) : 0);
		sum += baseCoverage * (increase ? increase * 10 : 0);
	}

	if (age < 45 && age > 34) {
		sum = baseCoverage + baseCoverage * (increase ? increase * (45 - age) : 0);
	}

	if (age > 46) {
		sum =
			baseCoverage + baseCoverage * (reduction ? reduction * (46 - age) : 0);
	}

	const minCoverage = MIN_COMPENSATION_PERCENT * baseCoverage;

	if (sum < minCoverage) {
		return toFixedNumber(minCoverage);
	}

	return toFixedNumber(sum);
}
